import { useState, useRef, useEffect } from 'react';

interface SaveToCalendarProps {
  title: string;
  location: string;
  detail: string;
  eventDate: Date | null;
  startHour?: number;
  durationMins?: number;
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toIcsDate(date: Date, hour: number, durationMins: number) {
  const start = new Date(date);
  start.setHours(hour, 0, 0, 0);
  const end = new Date(start.getTime() + durationMins * 60 * 1000);

  const fmt = (d: Date) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;

  return { start: fmt(start), end: fmt(end) };
}

function buildGoogleUrl(
  title: string,
  location: string,
  detail: string,
  date: Date,
  startHour: number,
  durationMins: number,
) {
  const { start, end } = toIcsDate(date, startHour, durationMins);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${start}/${end}`,
    details: detail,
    location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildIcsBlob(
  title: string,
  location: string,
  detail: string,
  date: Date,
  startHour: number,
  durationMins: number,
) {
  const { start, end } = toIcsDate(date, startHour, durationMins);
  const uid = `${Date.now()}@mommymingle`;
  const content = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//The Mommy Mingle//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${start}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${title}`,
    `LOCATION:${location}`,
    `DESCRIPTION:${detail.replace(/\n/g, '\\n')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return new Blob([content], { type: 'text/calendar;charset=utf-8' });
}

export default function SaveToCalendar({
  title,
  location,
  detail,
  eventDate,
  startHour = 10,
  durationMins = 60,
}: SaveToCalendarProps) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleSaved() {
    setSaved(true);
    setOpen(false);
    setTimeout(() => setSaved(false), 2200);
  }

  function downloadIcs() {
    if (!eventDate) return;
    const blob = buildIcsBlob(title, location, detail, eventDate, startHour, durationMins);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '-').toLowerCase()}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    handleSaved();
  }

  function openGoogle() {
    if (!eventDate) return;
    window.open(buildGoogleUrl(title, location, detail, eventDate, startHour, durationMins), '_blank');
    handleSaved();
  }

  // No date resolved (flex outing) — download .ics for today
  const calDate = eventDate ?? new Date();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Save to calendar"
        className={`w-7 h-7 flex items-center justify-center rounded-full border transition-all cursor-pointer
          ${saved
            ? 'border-emerald-400 bg-emerald-50 text-emerald-500'
            : 'border-stone-200 bg-white text-stone-400 hover:border-pink-300 hover:text-pink-500 hover:bg-pink-50'
          }`}
      >
        <i className={`text-xs ${saved ? 'ri-check-line' : 'ri-calendar-check-line'}`}></i>
      </button>

      {/* Saved flash */}
      {saved && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full pointer-events-none animate-fade-in">
          Saved!
        </span>
      )}

      {/* Popover */}
      {open && !saved && (
        <div className="absolute bottom-9 left-1/2 -translate-x-1/2 w-44 bg-white border border-stone-100 rounded-xl overflow-hidden z-50">
          <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wide px-3 pt-2.5 pb-1">
            Add to calendar
          </p>
          <button
            onClick={openGoogle}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center text-rose-500">
              <i className="ri-google-line text-sm"></i>
            </div>
            Google Calendar
          </button>
          <button
            onClick={downloadIcs}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center text-stone-500">
              <i className="ri-apple-line text-sm"></i>
            </div>
            Apple / Outlook (.ics)
          </button>
          <div className="h-2" />
        </div>
      )}
    </div>
  );
}
